/**
 * Where somebody is in Imagine Yourself There (screens/Imagine.tsx), kept so a
 * reload does not start them over.
 *
 * The photo step opens the camera or the photo library. Inside Facebook's and
 * Instagram's iOS browsers the camera is heavy enough that iOS may unload the
 * page behind it, and the page comes back reloaded — the same reload that put
 * a visitor back at the intro on 2026-10-14 (PostHog 01a13c17). Their road
 * survives that now (lib/profile-sync.ts), but the starter's own place and step
 * lived only in React state: they would return from taking their selfie to an
 * empty "Dream up a place", at the exact moment they had just done the hardest
 * thing on the screen.
 *
 * So the place and the step are written to localStorage (the storage that did
 * survive those reloads) as they change, and read back when the starter opens.
 * Only the steps before anything is made: once the post goes up, the road
 * records it (recordMade) and a reload finds the print waiting on Home, so the
 * record is cleared there. The photo itself is not kept — a reload during the
 * camera means it never arrived anyway, and the person simply takes it again.
 */
const KEY = 'kindling.imagine.v1';

/** A starter left this long ago is not resumed: it would be a stranger's half-sentence. */
export const RESUME_WITHIN_MS = 6 * 60 * 60 * 1000;

export type ImagineStep = 'place' | 'photo';

/** The idea from the intro (the `idea_step` experiment): the drawing is of them inside it, and there is no place step. */
export interface ImagineIdea { kind: string; about: string }

/** A blank of the idea roll: what the dice landed on, and what they wrote over it. */
export interface ImagineRollPart { rolled: string; mine: string; shown?: boolean }
/**
 * The idea roll (the `place_step` experiment's `machine` arm): a reload lands back on the same postcard, at
 * the same level, their words still in it.
 */
export interface ImagineRoll { who: ImagineRollPart; where: ImagineRollPart; doing: ImagineRollPart; level: number; shape?: string; dream?: string }
/** The sentence they went on with, which the photo step and the drawing take. */
export interface ImagineRollWords { who: string; where: string; doing: string }

export interface ImagineProgress {
  place: string;
  step: ImagineStep;
  at: number;
  idea?: ImagineIdea;
  roll?: ImagineRoll;
  /** the roll's sentence, once they went on with it */
  rolled?: ImagineRollWords;
}

const str = (v: unknown, max = 60) => (typeof v === 'string' ? v.slice(0, max) : '');

function wordsFrom(x: unknown): ImagineRollWords | undefined {
  const w = x as Partial<ImagineRollWords> | null | undefined;
  if (!w || !str(w.who).trim() || !str(w.where).trim() || !str(w.doing).trim()) return undefined;
  return { who: str(w.who), where: str(w.where), doing: str(w.doing) };
}

function rollFrom(x: unknown): ImagineRoll | undefined {
  const r = x as Partial<Record<keyof ImagineRoll, unknown>> | null | undefined;
  const part = (p: unknown): ImagineRollPart | undefined => {
    const q = p as Partial<ImagineRollPart> | null | undefined;
    return q && str(q.rolled) ? { rolled: str(q.rolled), mine: str(q.mine), ...(q.shown === false ? { shown: false } : {}) } : undefined;
  };
  if (!r) return undefined;
  const who = part(r.who), where = part(r.where), doing = part(r.doing);
  if (!who || !where || !doing) return undefined;
  const level = typeof r.level === 'number' && r.level >= 1 && r.level <= 4 ? Math.floor(r.level) : 1;
  const dream = str(r.dream, 20);
  const shape = str(r.shape, 30);
  return { who, where, doing, level, ...(shape ? { shape } : {}), ...(dream ? { dream } : {}) };
}

function ideaFrom(x: unknown): ImagineIdea | undefined {
  const i = x as Partial<ImagineIdea> | null | undefined;
  if (!i || typeof i.about !== 'string' || !i.about.trim()) return undefined;
  return { kind: typeof i.kind === 'string' ? i.kind.slice(0, 40) : '', about: i.about.slice(0, 200) };
}

export function loadImagineProgress(now: number = Date.now()): ImagineProgress | null {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || 'null') as Partial<ImagineProgress> | null;
    if (!raw || typeof raw.place !== 'string' || typeof raw.at !== 'number') return null;
    if (raw.step !== 'place' && raw.step !== 'photo') return null;
    if (now - raw.at > RESUME_WITHIN_MS || raw.at > now + 60_000) return null;
    const place = raw.place.slice(0, 120);
    const idea = ideaFrom(raw.idea);
    // an idea path has no place step: it always resumes at the photo
    if (idea) return { place, step: 'photo', at: raw.at, idea };
    const roll = rollFrom(raw.roll);
    const rolled = wordsFrom(raw.rolled);
    // the photo step is about a place: without one there is nothing to resume to but the start
    return { place, step: raw.step === 'photo' && place.trim() ? 'photo' : 'place', at: raw.at, ...(roll ? { roll } : {}), ...(rolled ? { rolled } : {}) };
  } catch {
    return null;
  }
}

export function saveImagineProgress(p: { place: string; step: ImagineStep; idea?: ImagineIdea; roll?: ImagineRoll; rolled?: ImagineRollWords }, now: number = Date.now()): void {
  const idea = ideaFrom(p.idea);
  const roll = rollFrom(p.roll);
  const rolled = wordsFrom(p.rolled);
  try {
    localStorage.setItem(KEY, JSON.stringify({ place: p.place.slice(0, 120), step: p.step, at: now, ...(idea ? { idea } : {}), ...(roll ? { roll } : {}), ...(rolled ? { rolled } : {}) }));
  } catch { /* storage blocked: a reload starts the starter over, as before */ }
}

export function clearImagineProgress(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing kept to clear */ }
}
